'use client'
import LazyImage from '@/src/components/common/ui/LazyImage'
import { ChevronLeft, ChevronRight, Star } from '@mui/icons-material'
import { Box, Container, IconButton, Link, Typography } from '@mui/material'
import { useLocale } from 'next-intl'
import { useRef } from 'react'

export interface PopularHotel {
    id: string
    name: string
    location: string
    rating: number
    luxury: boolean
    imageUrl: string
    href: string
}

export interface PopularHotelsProps {
    title: string
    luxuryLabel: string
    offLabel: string
    hotels: PopularHotel[]
}

// Card sizing matches the Trending Hotels carousel so every homepage carousel is uniform.
const CARD_WIDTH = 280

// Pure presentation: popular-hotel carousel using the Trending Hotels card design (full-bleed
// image with a bottom gradient overlay), plus the ported luxury badge + "35% off" promo chip.
const PopularHotels: React.FC<PopularHotelsProps> = ({ title, luxuryLabel, offLabel, hotels }) => {
    const locale = useLocale()
    const isRTL = locale === 'ar'
    const scrollRef = useRef<HTMLDivElement>(null)

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollRef.current
        if (!container) return
        // Measure the real card width (responsive: ~4-up on desktop) + 16px gap.
        const firstCard = container.firstElementChild as HTMLElement | null
        const amount = (firstCard?.getBoundingClientRect().width ?? CARD_WIDTH) + 16
        const delta = direction === 'left' ? -amount : amount
        container.scrollBy({ left: isRTL ? -delta : delta, behavior: 'smooth' })
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
                <Typography component="h2" variant="h5" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.25rem', mb: 3 }}>
                    {title}
                </Typography>

                {/* Matches the offers carousel: 1200px row centered so the plain-chevron arrows
                    sit outside the cards in the gutter. */}
                <Box sx={{ position: 'relative', mx: 'auto', maxWidth: { xs: '100%', md: '1200px' } }}>
                    <IconButton
                        onClick={() => scroll('left')}
                        aria-label="Scroll left"
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            position: 'absolute',
                            insetInlineStart: -60,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 20,
                            width: 48,
                            height: 48
                        }}
                    >
                        {isRTL ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />}
                    </IconButton>
                    <IconButton
                        onClick={() => scroll('right')}
                        aria-label="Scroll right"
                        sx={{
                            display: { xs: 'none', md: 'flex' },
                            position: 'absolute',
                            insetInlineEnd: -60,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 20,
                            width: 48,
                            height: 48
                        }}
                    >
                        {isRTL ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />}
                    </IconButton>

                    <Box
                        ref={scrollRef}
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            direction: isRTL ? 'rtl' : 'ltr',
                            pb: 1,
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        {hotels.map(hotel => (
                            <Link
                                key={hotel.id}
                                href={hotel.href}
                                target="_blank"
                                rel="noopener"
                                sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    height: '210px',
                                    width: { xs: '280px', md: 'calc((100% - 48px) / 4)' },
                                    minWidth: { xs: '250px', md: 0 },
                                    bgcolor: 'common.white',
                                    display: 'block',
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    scrollSnapAlign: 'start',
                                    flexShrink: 0
                                }}
                            >
                                {/* Luxury badge — start corner */}
                                {hotel.luxury && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            insetInlineStart: 8,
                                            bgcolor: 'warning.light',
                                            borderRadius: '20px',
                                            px: 1.5,
                                            py: 0.5,
                                            zIndex: 2
                                        }}
                                    >
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'textColors.primary' }}>
                                            {luxuryLabel}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Promo chip — end corner */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        insetInlineEnd: 8,
                                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                                        borderRadius: '20px',
                                        px: 1.5,
                                        py: 0.5,
                                        zIndex: 2
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'common.white' }}>
                                        {offLabel}
                                    </Typography>
                                </Box>

                                <LazyImage src={hotel.imageUrl} alt={hotel.name} sizes="(max-width: 768px) 250px, 280px" />

                                {/* Bottom gradient overlay with name + rating/location */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        p: 1.5,
                                        zIndex: 1,
                                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%)'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: 'common.white',
                                            mb: 0.5,
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {hotel.name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                            {Array.from({ length: hotel.rating }).map((_, index) => (
                                                <Star key={index} sx={{ fontSize: '0.875rem', color: '#FFD700', fill: '#FFD700' }} />
                                            ))}
                                        </Box>
                                        <Box sx={{ width: '1px', height: '12px', bgcolor: 'rgba(255, 255, 255, 0.5)' }} />
                                        <Typography
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 400,
                                                color: 'common.white',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {hotel.location}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Link>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export { PopularHotels }
export default PopularHotels
